import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OnHoldReason } from 'enums/OnHoldReason';
import { environment } from 'environments/environment';
import { SelectListItemModel } from 'models/SelectList';
import { InvoiceObjectionSearchRequestModel } from 'models/generated/Accounting.Api.Models.InvoiceObjectionSearchRequest';
import { OnHoldRequestModel } from 'models/generated/Accounting.Api.Models.OnHoldRequest';
import { CustomerLedgerEntryModel } from 'models/generated/BusinessCentral.Integration.Models.CustomerLedgerEntry';
import { IssuedReminderModel } from 'models/generated/BusinessCentral.Integration.Models.IssuedReminder';
import { InvoiceObjectionModel } from 'models/generated/Entities.Accounting.InvoiceObjection';
import { firstValueFrom } from 'rxjs';
import { Base64File, FileStorageService } from './file-storage.service';
import { BookedEntryModel } from 'models/generated/Economic.Integration.Clients.EconomicBookedEntriesClient.BookedEntry';
import { BcDocumentType } from 'enums/BcDocumentType';
import { BillingDocumentLineModel } from 'models/generated/Accounting.IntegrationAbstractions.BillingDocumentLine';
import { LedgerHeaderModel } from 'models/generated/Entities.Accounting.LedgerHeader';
import { AccountingService } from 'services/accounting.service';
import { BrandService } from './brand.service';

export interface LedgerEntryDisplayModel {
	brandId?: string;
	documentNo: string;
	documentId?: string;
	documentType: BcDocumentType;
	postingDate?: Date;
	dueDate?: Date;
	amount?: number;
	amountInclVat?: number;
	open?: boolean;
	customerNo: string;
	entryNo?: number;
	remainingAmountInclVat?: number;
	description?: string;
	ledgerHeader?: LedgerHeaderModel;
}

@Injectable()
export class InvoicingService {

	private readonly apiUrl = `${environment.gatewayUrl}/api/invoicing`;
	private readonly http = inject(HttpClient);
	private readonly accountingService = inject(AccountingService);
	private readonly fileStorageService = inject(FileStorageService);
	private readonly brandService = inject(BrandService);

	// Select List
	private getSortedDimensionsSelectList(dimensionValues: DimensionValue[], dimension: string): SelectListItemModel[] {
		if (dimensionValues.length === 0) throw new Error(`Missing ${dimension} dimensions`);
		return dimensionValues.sort((d1, d2) => d1.code.localeCompare(d2.code)).filter(d => !d.blocked).map(dv => new SelectListItemModel(dv.code, dv.name != '' ? dv.name : dv.code));
	}

	getProductDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getProductDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "product"));
	}

	getDepartmentDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getDepartmentDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "department"));
	}

	getCompanyDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getCompanyDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "company"));
	}

	getBcItemsSelectList(): Promise<SelectListItemModel[]> {
		return this.getBcItems().then(items => {
			if (items.length === 0) throw new Error('Missing business central items');
			return items.map(item => new SelectListItemModel(item.no, item.description));
		});
	};

	// Dimensions list
	private getProductDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/PRODUKT`));
	}

	private getDepartmentDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/AFDELING`));
	}

	private getCompanyDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/VIRKSOMHEDSTYPE`));
	}

	private getBcItems(): Promise<BusinessCentralItem[]> {
		return firstValueFrom(this.http.get<BusinessCentralItem[]>(`${this.apiUrl}/items`));
	}

	exportLedgerPosts(ledgerPostInfos: { postId: string, description: string }[]): Promise<{ referenceId: string, total: number }> {
		return firstValueFrom(this.http.post<{ referenceId: string, total: number }>(`${this.apiUrl}/register-ledger-posts`, ledgerPostInfos));
	}

	getBusinessCentralLedgerEntriesByCompanyId(companyId: string, brandId: string): Promise<CustomerLedgerEntryModel[]> {
		return firstValueFrom(this.http.get<CustomerLedgerEntryModel[]>(`${this.apiUrl}/business-central-ledger-entries-by-company/${companyId}`, { params: { brandId } }));
	}

	getEconomicLedgerEntriesByCompanyId(companyId: string, brandId: string): Promise<BookedEntryModel[]> {
		return firstValueFrom(this.http.get<BookedEntryModel[]>(`${this.apiUrl}/e-conomic-ledger-entries-by-company/${companyId}`, { params: { brandId } }));
	}
		
	getCustomerLedgerEntriesByDocumentNos(documentNos: string[], brandId: string): Promise<CustomerLedgerEntryModel[]> {
		return firstValueFrom(this.http.post<CustomerLedgerEntryModel[]>(`${this.apiUrl}/customer-ledger-entries-by-documentnos`, documentNos, { params: { brandId } }));
	}

	getInvoiceObjections(documents: { documentNo?: string, brandId?: string; }[]): Promise<InvoiceObjectionModel[]> {
		if (documents.length == 0) return new Promise(() => new Array<InvoiceObjectionModel>());
		return firstValueFrom(this.http.post<InvoiceObjectionModel[]>(`${this.apiUrl}/get-invoice-objections`, documents));
	}
	
	async getIssuedReminders(customerNos: string[]): Promise<IssuedReminderModel[]> {
		if (customerNos.length == 0) return new Array<IssuedReminderModel>();
		return await firstValueFrom(this.http.post<IssuedReminderModel[]>(`${this.apiUrl}/reminders`, customerNos));
	}
	
	createOrUpdateInvoiceObjection(invoiceObjection: InvoiceObjectionModel, setInvoiceOnHold: boolean): Promise<InvoiceObjectionModel> {
		return firstValueFrom(this.http.post<InvoiceObjectionModel>(`${this.apiUrl}/invoice-objections?setInvoiceOnHold=${setInvoiceOnHold}`, invoiceObjection));
	}

	editInvoiceDueDate(documentNo: string, brandId: string, dueDate: Date) {
		return firstValueFrom(this.http.put<void>(`${this.apiUrl}/due-date?documentNo=${documentNo}&brandId=${brandId}`, dueDate));
	}

	searchInvoiceObjections(searchRequest: InvoiceObjectionSearchRequestModel): Promise<InvoiceObjectionModel[]> {
		return firstValueFrom(this.http.post<InvoiceObjectionModel[]>(`${this.apiUrl}/search-invoice-objections`, searchRequest));
	}

	getCreditMemoIdsByCompanyId(companyId: string, brandId: string): Promise<Record<string, string>> {
		return firstValueFrom(this.http.get<Record<string, string>>(`${this.apiUrl}/creditmemo-ids-by-company/${companyId}`, { params: { brandId } }));
	}

	getPostedDocumentLines(documentNo: string, type: BcDocumentType, brandId: string): Promise<BillingDocumentLineModel[]> {
		return firstValueFrom(this.http.get<BillingDocumentLineModel[]>(`${this.apiUrl}/posted-document-lines/${brandId}/${documentNo}`, { params: { type } }));
	}

	getSalesInvoiceIdsByCompanyId(companyId: string, brandId: string): Promise<Record<string, string>> {
		return firstValueFrom(this.http.get<Record<string, string>>(`${this.apiUrl}/salesinvoice-ids-by-company/${companyId}`, { params: { brandId }}));
	}

	sendSalesInvoice(documentId: string, brandId: string) {
		return firstValueFrom(this.http.post<void>(`${this.apiUrl}/send-salesinvoice-by-id/${documentId}`, null, { params: { brandId }}));
	}
	sendCreditMemo(documentId: string, brandId: string) {
		return firstValueFrom(this.http.post<void>(`${this.apiUrl}/send-creditmemo-by-id/${documentId}`, null, { params: { brandId } }));
	}
	downloadSalesInvoice(documentId: string, filename: string, brandId: string) {
		this.http.get<Base64File>(`${this.apiUrl}/download-salesinvoice-by-id/${documentId}`, { params: { brandId }}).subscribe(response => {
			this.fileStorageService.downloadFileByBase64(response, filename || 'faktura.pdf');
		});
	}
	downloadCreditMemo(documentId: string, filename: string, brandId: string) {
		this.http.get<Base64File>(`${this.apiUrl}/download-creditmemo-by-id/${documentId}`, { params: { brandId } }).subscribe(response => {
			this.fileStorageService.downloadFileByBase64(response, filename || 'kreditnota.pdf');
		});
	}

	updateOnHold(brandId: string, documentNo: string, companyId: string, onHoldReason?: OnHoldReason) {
		const request = new OnHoldRequestModel();
		request.brandId = brandId;
		request.documentNo = documentNo;
		request.onHoldReason = onHoldReason;
		request.companyId = companyId;
		return firstValueFrom(this.http.post(`${this.apiUrl}/on-hold`, request));
	}

	getInvoicesWithOverriddenDueDates(brandId: string) {
		return firstValueFrom(this.http.get<CustomerInvoiceInformation[]>(`${this.apiUrl}/invoices-with-overridden-due-dates`, { params: { brandId } }));
	}

	async getLedgerEntriesForCompany(companyId: string): Promise<LedgerEntryDisplayModel[]> {

		const goBrand = this.brandService.brands.find(b => b.shortName === 'GO')?.id!;
		const wkBrand = this.brandService.brands.find(b => b.shortName === 'WK')?.id!;

		const ledgerEntryGroups = await Promise.all([
			// TODO (#23771): Single endpoint with common interface and brandId property. Should probably also include the salesInvoiceIds and creditMemoIds in the same request (since documentId and brandId is a part of the interface - saves using the lookup objects)
			this.getBusinessCentralLedgerEntriesByCompanyId(companyId, goBrand)
				.then((entries: LedgerEntryDisplayModel[]) => {
					entries.forEach(e => e.brandId = goBrand);
					return entries;
				}).then(entries => this.getLedgerHeaders(entries, goBrand)),
			this.getEconomicLedgerEntriesByCompanyId(companyId, wkBrand).then(entries =>
				entries.map(e => {
					return {
						brandId: wkBrand,
						documentNo: e.type == 1 ? (e.customerInvoiceNumber?.toString() ?? '') : e.type == 2 ? e.entryNumber?.toString() : '',
						documentId: e.type == 1 ? e.customerInvoiceNumber?.toString() : undefined, // Allows downloading invoice as PDF
						documentType: e.type == 1 ? (e.amount >= 0 ? BcDocumentType.Invoice : BcDocumentType.CreditMemo) : e.type == 2 ? BcDocumentType.Payment : BcDocumentType.Unknown,
						postingDate: e.date,
						dueDate: e.dueDate,
						amount: e.amountExclVat,
						amountInclVat: e.amountInclVat,
						open: e.remainder != 0,
						customerNo: e.customerNumber!.toString(),
						entryNo: e.entryNumber,
						remainingAmountInclVat: e.remainder,
						description: e.text
					} as LedgerEntryDisplayModel;
				})).then(entries => this.getLedgerHeaders(entries, wkBrand)),
		]);

		return ledgerEntryGroups.flat();
	}

	async getLedgerHeaders(entries: LedgerEntryDisplayModel[], brandId: string) {
		const postedDocuments = entries.filter(e => e.documentType == BcDocumentType.Invoice || e.documentType == BcDocumentType.CreditMemo);
		const ledgerHeaders = await this.accountingService.getLedgerHeadersByPostedDocumentNumbers(postedDocuments.map(e => e.documentNo), brandId);
		for (const entry of entries)
			entry.ledgerHeader = ledgerHeaders.find(h => h?.postedDocumentNo == entry.documentNo && h?.brandId == brandId);

		return entries;
	}
}

export interface CustomerInvoiceInformation {
	companyId: string;
	customerNumber: string;
	ledgerHeaders: LedgerHeaderModel[];
}

export class BusinessCentralItem {
	no!: string;
	description!: string;
}
export class DimensionValue {
	dimensionCode!: string;
	dimensionName!: string;
	code!: string;
	name!: string;
	blocked!: boolean;
}
